<template>
  <div style="padding: 10px; overflow-y: auto; height: 100%;">
    <div v-if="loading" style="text-align: center; margin-top: 2em;">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
    </div>
    <div v-else-if="checkouts.length === 0" style="padding: 20px; text-align: center; color: gray;">
      No checked out bags found.
    </div>
    <table v-else class="data-table" cellspacing="0" style="width: 100%;">
      <tr class="table-headers">
        <th style="text-align: left;">Date</th>
        <th style="text-align: left;">Mission Number</th>
        <th style="text-align: left;">Executor</th>
        <th style="text-align: left;">Status</th>
        <th style="text-align: right;"></th>
      </tr>
      <tbody class="table-data" v-for="(item, index) in checkouts" :key="index">
        <tr class="drugtablerow" style="height: 40px; cursor: pointer;" @click="item.expand = !item.expand" :class="[item.expand ? 'open-transfer-row' : '']">
          <td style="padding: 5px; border-bottom: 1px solid #ccc;">
            <i :class="item.expand ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" style="margin-right: 5px; font-size: 0.8em;"></i>
            {{ new Date(item.created_at).toLocaleString() }}
          </td>
          <td style="padding: 5px; border-bottom: 1px solid #ccc;">{{ item.mission ? item.mission : 'N/A' }}</td>
          <td style="padding: 5px; border-bottom: 1px solid #ccc;">
            <!-- personal_information is what PersonSimpleResource emits. latestPersonalInformation is
                 the relation name the API loads it through and never reaches the payload. -->
            {{ item.executor && item.executor.personal_information ? item.executor.personal_information.firstname + ' ' + item.executor.personal_information.lastname : 'N/A' }}
          </td>
          <td style="padding: 5px; border-bottom: 1px solid #ccc;">
            <span :class="item.status === 'open' ? 'green-tag' : 's8-tag'" style="padding: 3px 8px; border-radius: 4px; font-weight: bold;">
              {{ item.status.toUpperCase() }}
            </span>
          </td>
          <td style="padding: 5px; border-bottom: 1px solid #ccc; text-align: right;">
            <button class="submit-button blue-button" style="margin: 0; padding: 5px 15px; font-size: 0.9em;" @click.stop="openCheckIn(item)">Check In</button>
          </td>
        </tr>
        <tr v-if="item.expand" class="expanded-content-row">
          <td colspan="5" style="padding: 10px; background-color: #f9f9f9; border-bottom: 1px solid #ccc;">
            <div style="margin-bottom: 5px;" v-if="item.note && item.note.text"><span style="font-weight: bold;">Notes:</span> {{ item.note.text }}</div>
            <div v-if="item.drugGroups && item.drugGroups.length > 0">
              <span style="font-weight: bold; display: block; margin-bottom: 8px;">Medications:</span>
              <ul style="margin: 0 0 0 20px; padding: 0;">
                <li v-for="(drug, dIndex) in item.drugGroups" :key="dIndex" style="margin-bottom: 6px; display: flex; align-items: flex-start;">
                  <strong style="white-space: nowrap; margin-right: 8px; flex-shrink: 0; margin-top: 2px;">
                    {{ drug.qty }} {{ drug.form ? drug.form : 'x' }}
                  </strong>
                  <div @click="drug.expanded = !drug.expanded" style="flex: 1; cursor: pointer; line-height: 1.4;" :style="{ whiteSpace: drug.expanded ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }" :title="drug.expanded ? 'Click to collapse' : 'Click to expand'">
                    <span v-if="drug.generic && drug.generic.toLowerCase() !== drug.name.toLowerCase()">
                      <span style="color: #666; font-size: 0.9em; margin-right: 4px;">({{ drug.generic }})</span> {{ drug.name }}
                    </span>
                    <span v-else>
                      {{ drug.name }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>
            <div v-else style="color: gray; font-style: italic; margin-bottom: 8px;">No medications recorded.</div>
          </td>
        </tr>
      </tbody>
    </table>
    
    <BagCheckinModal
        v-if="showCheckinModal"
        :preselectedBagId="selectedBagToVerify"
        @close="closeCheckinModal"
    />

    <BagCheckoutModal
        v-if="showCheckoutModal"
        @close="closeCheckoutModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import BagCheckinModal from '@/components/modals/BagCheckinModal.vue';
import BagCheckoutModal from '@/components/modals/BagCheckoutModal.vue';

export default {
  name: 'CheckedOutBags',
  components: {
    BagCheckinModal,
    BagCheckoutModal
  },
  data() {
    return {
      loading: false,
      checkouts: [],
      showCheckinModal: false,
      showCheckoutModal: false,
      selectedBagToVerify: null
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    openCheckIn(item) {
      this.selectedBagToVerify = item.uuid;
      this.showCheckinModal = true;
    },
    closeCheckinModal(_closed, status) {
      this.showCheckinModal = false;
      this.selectedBagToVerify = null;
      if (status === 'success') {
        this.fetchData();
      }
    },
    closeCheckoutModal(_closed, status) {
      this.showCheckoutModal = false;
      if (status === 'success') {
        this.fetchData();
      }
    },
    getDrugGroups(units) {
      if (!units) return [];
      const groups = {};
      units.forEach(u => {
        const key = u.stock_item ? u.stock_item.uuid : 'unknown';
        if (!groups[key]) {
          const alias = u.stock_item && u.stock_item.alias ? u.stock_item.alias : null;
          groups[key] = {
            qty: 0,
            name: alias ? alias.name : 'Unknown Medication',
            generic: alias ? alias.generic_name : '',
            form: alias && alias.form ? alias.form : ''
          };
        }
        let current = parseFloat(u.current_qty);
        let quantity = parseFloat(u.quantity);
        groups[key].qty += !isNaN(current) ? current : (!isNaN(quantity) ? quantity : 0);
      });
      return Object.values(groups);
    },
    fetchData() {
      this.loading = true;
      axios.post('/get-open-bag-checkouts', {}, this.$store.state.header).then(res => {
        this.checkouts = (res.data.data || []).map(item => {
            item.expand = false;
            item.drugGroups = this.getDrugGroups(item.units).map(d => ({ ...d, expanded: false }));
            return item;
        });
        this.loading = false;
      }).catch(err => {
        this.loading = false;
        console.error("Failed to fetch checked out bags", err);
      });
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";
.open-transfer-row {
  background-color: #e5f1fe !important;
}
.expanded-content-row td {
  background-color: #fafafa !important;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
}
</style>
